import { t } from "../utils/i18n.ts";
import { COLORS } from "../core/constants.ts";
import { ENEMY_PALETTE } from "../core/palette.ts";
import type { HighscoreEntry } from "../core/MetaState.ts";

export function formatScoreDate(iso?: string): string {
  if (!iso || iso.length < 10) return "--/--/--";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y!.slice(2)}`;
}

function etch(t: string, lr: string, ac?: string): string {
  const style = ac ? ` style="--etch-ac:${ac}"` : "";
  return `<span class="boot-etch ${t} ${lr}"${style}></span>`;
}

/** Gold star+triangle trophy emblem for the GLOBAL DEFENSE LOG panel — draws the eye to
 *  the records. Inline SVG (transparent bg → sits on our dark panel, no white). */
const RECORDS_STAR = `<svg viewBox="0 0 120 120" aria-hidden="true">
  <defs>
    <linearGradient id="rstar-gold" x1="0" y1="0" x2="0.25" y2="1">
      <stop offset="0" stop-color="#FFF4CC"/><stop offset="0.32" stop-color="#FFD96B"/>
      <stop offset="0.62" stop-color="#E8A833"/><stop offset="1" stop-color="#9A6512"/>
    </linearGradient>
    <linearGradient id="rstar-tri" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#FFE49A"/><stop offset="1" stop-color="#7A4A0E"/>
    </linearGradient>
  </defs>
  <polygon points="52,24 8,106 104,106" fill="none" stroke="url(#rstar-tri)" stroke-width="3.5" stroke-linejoin="round" opacity="0.9"/>
  <polygon points="64,14 74.6,43.4 105.9,44.4 81.1,63.6 89.9,93.6 64,76 38.1,93.6 46.9,63.6 22.2,44.4 53.4,43.4" fill="url(#rstar-gold)" stroke="#7A4A0E" stroke-width="2.2" stroke-linejoin="round"/>
  <polygon points="64,28 71.1,48.3 92.5,48.7 75.4,61.7 81.6,82.3 64,70 46.4,82.3 52.6,61.7 35.5,48.7 57,48.3" fill="none" stroke="#B8841E" stroke-width="1.4" stroke-linejoin="round" opacity="0.7"/>
</svg>`;

/** Generate the boot sequence HTML template */
/**
 * The five local rows for ONE population. Exported because the board's threat
 * level switcher re-renders this pane in place — the same markup must come from
 * one function, or the switched view would drift from the first paint.
 */
export function renderLocalScoreRows(highscores?: HighscoreEntry[]): string {
  const ranks = ["#1", "#2", "#3", "#4", "#5"];
  return ranks
    .map((rank, i) => {
      const entry = highscores?.[i];
      const name = entry?.initials ?? "---";
      const pts = entry ? entry.score.toLocaleString() : "000,000";
      return `<div class="boot-score-row"><span class="boot-score-rank">${rank}</span><span class="boot-score-name">${name}</span><span class="boot-score-date">${formatScoreDate(
        entry?.date
      )}</span><span class="boot-score-pts">${pts}</span></div>`;
    })
    .join("\n");
}

export function getBootHTML(
  highscores?: HighscoreEntry[],
  globalMarkup?: string,
  /** Display name of the population shown first (e.g. "CONTAINMENT"). */
  levelLabel?: string
): string {
  const localRows = renderLocalScoreRows(highscores);

  const resolvedGlobalMarkup =
    globalMarkup ?? `<div class="boot-score-offline">GLOBAL - OFFLINE</div>`;

  return `
    <div class="boot-atmosphere">
      <div class="boot-atmo-grid"></div>
      <div class="boot-atmo-floor"></div>
      <div class="boot-atmo-haze"></div>
      <div class="boot-atmo-vig"></div>
    </div>

    <div class="boot-grid">
      <!-- LEFT: DEFENSE / STATUS (green) -->
      <div class="boot-col left">
        <div class="boot-panel" data-phase="2">
          ${etch("t", "l")}${etch("t", "r")}<div class="boot-scan"></div>
          <div class="boot-ph"><span class="dot"></span>MAINFRAME ONLINE</div>
          <div class="boot-pb">
            <div class="boot-kv"><span class="k">SYS ID</span><span class="v g">MF-AXIOM-77</span></div>
            <div class="boot-kv"><span class="k">UPTIME</span><span class="v" id="boot-uptime">1025:57:19</span></div>
            <div class="boot-kv"><span class="k">SEC LVL</span><span class="v a">OMEGA</span></div>
            <div class="boot-sub r">\u26A1 THREAT ALERT</div>
            <div class="boot-brow"><span class="k">VECTORS</span><span class="boot-dbar r"><i id="boot-vectors-bar" style="width:77%"></i></span><span class="v r" id="boot-vectors">49</span></div>
            <div class="boot-brow"><span class="k">FIREWALL</span><span class="boot-dbar"><i id="boot-firewall-bar" style="width:88%"></i></span><span class="v g" id="boot-firewall">88%</span></div>
          </div>
        </div>

        <div class="boot-records-wrap">
        <div class="boot-panel boot-panel--records" data-phase="2">
          ${etch("t", "l")}<div class="boot-scan"></div>
          <!-- E5 (owner 2026-08-18): ONE name across every register — panel,
               run-end copy, pre-run copy and Apple's boards all say GLOBAL
               DEFENSE LOG; the threat level stays visible as the population
               row below the tabs. -->
          <div class="boot-ph"><span class="dot"></span>GLOBAL DEFENSE LOG</div>
          <div class="boot-pb">
            <div class="boot-score-tabs">
              <button id="boot-scores-local-btn" class="boot-score-tab" type="button">LOCAL</button>
              <button id="boot-scores-global-btn" class="boot-score-tab" type="button">GLOBAL</button>
            </div>
            <!-- The POPULATION switcher (owner spec 2026-08-17). Local, D1 and
                 Game Center all answer for whatever this row shows — a score is
                 only ever compared inside its own threat level. Purely a VIEW
                 control: it never changes the level the next run will be
                 played at (that is the deploy selector's job). -->
            <div class="boot-score-level" role="group" aria-label="Threat level">
              <button id="boot-level-prev" class="boot-score-level-arrow" type="button" aria-label="Previous threat level">&lsaquo;</button>
              <span id="boot-level-label" class="boot-score-level-label">${
                levelLabel ?? ""
              }</span>
              <button id="boot-level-next" class="boot-score-level-arrow" type="button" aria-label="Next threat level">&rsaquo;</button>
            </div>
            <div id="boot-scores-local" class="boot-score-panel">
              ${localRows}
            </div>
            <div id="boot-scores-global" class="boot-score-panel">
              ${resolvedGlobalMarkup}
            </div>
          </div>
        </div>
        <div class="boot-records-star" aria-hidden="true">${RECORDS_STAR}</div>
        </div>

        <div class="boot-panel" data-phase="2">
          ${etch("t", "l")}${etch("b", "l")}<div class="boot-scan"></div>
          <div class="boot-ph"><span class="dot"></span>SYSTEM STATUS</div>
          <div class="boot-pb">
            <div class="boot-stat">CORE ROUTINES <span class="boot-ok"><b></b>OK</span></div>
            <div class="boot-stat">MEMORY BANKS <span class="boot-ok"><b></b>OK</span></div>
            <div class="boot-stat">I/O CHANNELS <span class="boot-ok"><b></b>OK</span></div>
            <div class="boot-stat">DEFENSE NODES <span class="boot-ok"><b></b>OK</span></div>
            <div class="boot-stat">AI CORE <span class="boot-ok"><b></b>OK</span></div>
          </div>
        </div>
      </div>

      <!-- CENTER: AI CORE + HERO + TAGLINE + CTA -->
      <div class="boot-col center" data-phase="2">
        <div class="boot-aicore" id="boot-aicore">
          <div class="boot-beams"><span class="boot-beam g"><i></i></span><span class="boot-beam r"><i></i></span></div>
          <div class="boot-aifig p" id="boot-fig-vera"></div>
          <div class="boot-hex">
            <div class="shield"></div>
            <svg viewBox="0 0 100 100"><polygon points="50,6 90,28 90,72 50,94 10,72 10,28" fill="none" stroke="${
              COLORS.SENTINEL_GREEN
            }" stroke-width="3"/><polygon points="50,20 78,36 78,64 50,80 22,64 22,36" fill="none" stroke="#1C7A12" stroke-width="2"/></svg>
            <div class="core"></div>
            <div class="boot-hexlabel">MAINFRAME</div>
          </div>
          <div class="boot-aifig o" id="boot-fig-fenrir"></div>
          <div class="boot-activeline">VERA <b>\u27F6</b> CORE // DEFENSE LINK ACTIVE <span class="r">// FENRIR REPELLED</span></div>
        </div>

        <div class="boot-title-block">
          <div class="boot-logowrap"><img class="boot-logo" src="/img/aiwars-logo.png" alt="AI WARS" /></div>
          <!-- BL-226 — publisher brand: the studio the game launches under. -->
          <div class="boot-brand"><span class="br">▸</span> peerapps <span class="br">◂</span></div>
        </div>

        <div class="boot-tag">
          <div class="lead"><span class="br">||</span> // SAVE THE MAINFRAME // <span class="br">||</span></div>
          <div class="story"><span class="r">${t("boot.lore1")}</span> ${t(
    "boot.lore2"
  )}<br>${t("boot.lore3")}</div>
          <div class="ver">v3.7.1 // AXIOM-77 // 2084</div>
        </div>

        <div class="boot-cta-block">
          <div class="boot-term"><span class="p">root@axiom-77</span>:~$ ./start_defense<span class="car">_</span></div>
          <!-- F15-4 \u2014 the prompt names the input the device actually has. BOTH
               strings ship; a pointer:coarse media query picks one (see
               BootSequenceStyles). A CSS swap, not a JS branch: the boot screen
               is built once per language, so a JS read of the pointer type at
               build time would freeze the wrong answer for a device whose
               primary pointer changes (an iPad that gains a trackpad).
               NOTE: no backticks in here \u2014 this comment sits inside a template
               literal, and one would close it (measured: it did). -->
          <button id="boot-press" type="button"><span class="br">\u258E</span> <span class="boot-press-key">${t(
            "boot.pressKey"
          )}</span><span class="boot-press-tap">${t(
    "boot.tapKey"
  )}</span> <span class="br">\u258E</span><span class="foil"></span></button>
        </div>
      </div>

      <!-- RIGHT: THREAT / METRICS -->
      <div class="boot-col right">
        <div class="boot-panel crimson" data-phase="2">
          ${etch("t", "l", `var(--crimson,${ENEMY_PALETTE.final_boss})`)}${etch(
    "t",
    "r",
    `var(--crimson,${ENEMY_PALETTE.final_boss})`
  )}<div class="boot-scan"></div>
          <div class="boot-ph r"><span class="dot"></span>THREAD INTRUSIONS</div>
          <div class="boot-pb">
            <div class="boot-brow"><span class="k">.ROOT/SYNAPSE</span><span class="boot-dbar r"><i id="boot-intr-1" style="width:100%"></i></span><span class="v r">12</span></div>
            <div class="boot-brow"><span class="k">.NANO/WORM</span><span class="boot-dbar r"><i id="boot-intr-2" style="width:75%"></i></span><span class="v r">09</span></div>
            <div class="boot-brow"><span class="k">.LOGIC/LEECH</span><span class="boot-dbar r"><i id="boot-intr-3" style="width:58%"></i></span><span class="v r">07</span></div>
            <div class="boot-brow"><span class="k">.DATA/SIPHON</span><span class="boot-dbar r"><i id="boot-intr-4" style="width:92%"></i></span><span class="v r">11</span></div>
            <div class="boot-brow"><span class="k">.CORE/BREAKER</span><span class="boot-dbar r"><i id="boot-intr-5" style="width:67%"></i></span><span class="v r">08</span></div>
          </div>
        </div>

        <div class="boot-panel" data-phase="2">
          ${etch("t", "l")}${etch("t", "r")}<div class="boot-scan"></div>
          <div class="boot-ph"><span class="dot"></span>AI CORE METRICS</div>
          <div class="boot-pb">
            <div class="boot-sub first">\u25A3 AI PERFORMANCE</div>
            <div class="boot-kv"><span class="k">INFERENCE</span><span class="v g" id="boot-infer">8.51 GFLOPS</span></div>
            <div class="boot-brow"><span class="k">ACCURACY</span><span class="boot-dbar"><i id="boot-acc-bar" style="width:99.5%"></i></span><span class="v g" id="boot-acc">99.5%</span></div>
            <div class="boot-kv"><span class="k">LATENCY</span><span class="v" id="boot-latency">0.3ms</span></div>
            <div class="boot-kv"><span class="k">THREAT LVL</span><span class="v r">HIGH</span></div>
            <div class="boot-sub">\u25A3 HARDWARE</div>
            <div class="boot-brow"><span class="k">CPU TEMP</span><span class="boot-dbar"><i id="boot-cpu-bar" style="width:41%"></i></span><span class="v a" id="boot-cpu">41.2\u00B0C</span></div>
            <div class="boot-brow"><span class="k">RAM USAGE</span><span class="boot-dbar"><i id="boot-ram-bar" style="width:73%"></i></span><span class="v" id="boot-ram">73%</span></div>
            <div class="boot-kv"><span class="k">GPU CLOCK</span><span class="v" id="boot-gpu">2.0 GHz</span></div>
            <div class="boot-kv"><span class="k">DISK I/O</span><span class="v" id="boot-disk">267 MB/s</span></div>
          </div>
        </div>

        <div class="boot-panel" data-phase="2">
          ${etch("t", "r")}${etch("b", "r")}<div class="boot-scan"></div>
          <div class="boot-ph"><span class="dot"></span>COMMAND QUEUE</div>
          <div class="boot-pb">
            <div class="boot-cmd">&gt; scan_grid --deep</div>
            <div class="boot-cmd">&gt; init_defenses -f</div>
            <div class="boot-cmd">&gt; alloc_resources 0xFF</div>
            <div class="boot-cmd">&gt; analyze_threats -v</div>
            <div class="boot-cmd dim">&gt; awaiting_input...<span class="car">_</span></div>
          </div>
        </div>
      </div>
    </div>

    <div class="boot-footer" data-phase="2">
      <span>${__APP_VERSION__}</span>
      <span class="clr"><b></b> CLEARANCE: OMEGA <b></b></span>
      <span id="boot-time">2084-06-07 19:01:34</span>
    </div>

    <div class="boot-crt"></div>
    <div class="boot-post" id="boot-post"></div>
  `;
}
